// src/utils/exportUtils.js
import * as XLSX from 'xlsx';
import { toZonedTime, format } from 'date-fns-tz';

const getReportName = (reportType) => {
    switch (reportType) {
        case 'WORK_ONLY': return 'Work_Logs';
        case 'PAYROLL_ONLY': return 'Payroll_Statement';
        case 'COMBINED': return 'Summary_Report';
        default: return 'Report';
    }
};

const getDateLabel = (dateFilter, customDate) => {
    const now = new Date();
    const month = now.toLocaleString('default', { month: 'long' });
    const year = now.getFullYear();

    switch (dateFilter) {
        case 'today': return format(now, 'MM-dd-yyyy');
        case 'month': return `${month}_${year}`;
        case 'firstHalf': return `${month}_1-15_${year}`;
        case 'secondHalf': {
            const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
            return `${month}_16-${lastDay}_${year}`;
        }
        case 'custom':
            if (customDate?.start && customDate?.end) {
                return `${customDate.start}_to_${customDate.end}`;
            }
            return 'custom';
        default:
            return 'report';
    }
};

const parseDurationToHours = (durationStr) => {
    if (!durationStr || durationStr === '-') return 0;
    const hMatch = durationStr.match(/(\d+)\s*h/);
    const mMatch = durationStr.match(/(\d+)\s*m/);
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
    const minutes = mMatch ? parseInt(mMatch[1], 10) : 0;
    return hours + minutes / 60;
};

const buildSummaryRows = (summary, payrollData, propertyName) => {
    if (!summary || Object.keys(summary).length === 0) return [];

    return Object.values(summary).map(s => {
        let payrollHours = '-';
        let notes = '';

        if (payrollData?.lines) {
            const line = payrollData.lines.find(l => l.employee_user_id === s.employee_user_id);
            if (line) {
                payrollHours = parseFloat(line.payroll_hours) || 0;
                notes = line.notes || '';
            }
        }

        const totalHours = parseDurationToHours(s.total_hours);
        const overtimeHours = parseDurationToHours(s.overtime_hours);

        return {
            'Motel': propertyName || '-',
            'Employee ID': s.employee_identifier || '-',
            'Employee Name': s.employee_name || '-',
            'Total Hours': totalHours ? totalHours.toFixed(2) : '-',
            'Regular Hours': overtimeHours > 0 ? (totalHours - overtimeHours).toFixed(2) : (totalHours ? totalHours.toFixed(2) : '-'),
            'Overtime Hours': overtimeHours > 0 ? overtimeHours.toFixed(2) : '-',
            'Payroll Hours': payrollHours !== '-' ? payrollHours.toFixed(2) : '-',
            'Notes': notes,
        };
    });
};

const buildTimeEntryRows = (timeEntries, userTimezone, propertyName) => {
    if (!timeEntries || timeEntries.length === 0) return [];

    return timeEntries.map(entry => {
        const isCancelled = entry.start_time === entry.end_time;

        let startDate = '-';
        let startTime = '-';
        let endTime = '-';

        if (entry.start_time) {
            try {
                const zonedStart = toZonedTime(entry.start_time, userTimezone);
                startDate = format(zonedStart, 'MM/dd/yyyy', { timeZone: userTimezone });
                startTime = format(zonedStart, 'hh:mm a', { timeZone: userTimezone });
            } catch (e) { /* keep defaults */ }
        }

        if (entry.end_time && !isCancelled) {
            try {
                const zonedEnd = toZonedTime(entry.end_time, userTimezone);
                endTime = format(zonedEnd, 'hh:mm a', { timeZone: userTimezone });
            } catch (e) { /* keep defaults */ }
        }

        const totalHours = isCancelled ? 0 : parseDurationToHours(entry.duration);
        const payableHours = isCancelled ? 0 : parseDurationToHours(entry.payable_duration || entry.duration);
        const overtimeHours = entry.overtime_hours ? parseDurationToHours(entry.overtime_hours) : 0;

        return {
            'Motel': propertyName || '-',
            'Employee ID': entry.employee_identifier || '-',
            'Employee': entry.employee_name || '-',
            'Date': startDate,
            'Start Time': startTime,
            'End Time': isCancelled ? 'VOID' : endTime,
            'Total Hours': isCancelled ? 'VOID' : (totalHours ? totalHours.toFixed(2) : '-'),
            'Regular Hours': isCancelled ? 'VOID' : (overtimeHours > 0 ? (totalHours - overtimeHours).toFixed(2) : (totalHours ? totalHours.toFixed(2) : '-')),
            'Overtime Hours': isCancelled ? 'VOID' : (overtimeHours > 0 ? overtimeHours.toFixed(2) : '-'),
            'Payable Hours': isCancelled ? 'VOID' : (payableHours ? payableHours.toFixed(2) : '-'),
            'Meal Deduction': entry.meal_deducted > 0 ? `${entry.meal_deducted} min` : '-',
            'Notes': entry.notes || '',
        };
    });
};

const triggerDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const generateCSV = ({ summary, timeEntries, reportType, dateFilter, customDate, userTimezone, propertyName }) => {
    const reportName = getReportName(reportType);
    const dateLabel = getDateLabel(dateFilter, customDate);
    const fileName = `${reportName}_${dateLabel}.csv`;

    const summaryRows = buildSummaryRows(summary, null, propertyName);
    const entryRows = buildTimeEntryRows(timeEntries, userTimezone, propertyName);

    let csvContent = '';

    if (summaryRows.length > 0) {
        csvContent += 'Payroll Summary\n';
        const summaryHeaders = Object.keys(summaryRows[0]);
        csvContent += summaryHeaders.join(',') + '\n';
        summaryRows.forEach(row => {
            csvContent += summaryHeaders.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',') + '\n';
        });
        csvContent += '\n';
    }

    if (entryRows.length > 0) {
        csvContent += 'Time Entries\n';
        const entryHeaders = Object.keys(entryRows[0]);
        csvContent += entryHeaders.join(',') + '\n';
        entryRows.forEach(row => {
            csvContent += entryHeaders.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',') + '\n';
        });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, fileName);

    return { fileName, blob };
};

export const generateExcel = ({ summary, timeEntries, reportType, dateFilter, customDate, userTimezone, propertyName }) => {
    const reportName = getReportName(reportType);
    const dateLabel = getDateLabel(dateFilter, customDate);
    const fileName = `${reportName}_${dateLabel}.xlsx`;

    const wb = XLSX.utils.book_new();

    const summaryRows = buildSummaryRows(summary, null, propertyName);
    if (summaryRows.length > 0) {
        const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
        summarySheet['!cols'] = [
            { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 30 }
        ];
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Payroll Summary');
    }

    const entryRows = buildTimeEntryRows(timeEntries, userTimezone, propertyName);
    if (entryRows.length > 0) {
        const entrySheet = XLSX.utils.json_to_sheet(entryRows);
        entrySheet['!cols'] = [
            { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
            { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 30 }
        ];
        XLSX.utils.book_append_sheet(wb, entrySheet, 'Time Entries');
    }

    const xlsxBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    triggerDownload(blob, fileName);

    return { fileName, blob };
};
